import { AnyObject } from '@common/types/queryState';
import { APICustomFieldDescription, FacetFieldConfig } from '../types';

export const generateBaseConfig = <TEntity = AnyObject>(
    cfd: APICustomFieldDescription[],
): FacetFieldConfig<TEntity>[] => {
    return cfd
        .filter((def) => !def.hidden)
        .map(
            (def) =>
                ({
                    key: def.key as keyof TEntity & string,
                    type: def.type,
                    stickyMode: 'auto',
                    order: (def.order ?? 0) + 100,
                    canUnpin: true,
                    presets: [],
                    ...(def.type === 'range'
                        ? {
                              unit: '',
                              rangeLimits: { min: 0, max: 10000, step: 1 },
                          }
                        : {}),
                }) as FacetFieldConfig<TEntity>,
        );
};

export const mergeConfigs = <TEntity>(
    base: FacetFieldConfig<TEntity>[],
    overrides: FacetFieldConfig<TEntity>[],
): FacetFieldConfig<TEntity>[] => {
    const overrideMap = new Map(
        overrides.map((override, index) => [
            override.key,
            { order: index, canUnpin: false, ...override },
        ]),
    );

    const merged = base.map((dynamic) => {
        const override = overrideMap.get(dynamic.key);
        if (!override) return dynamic;

        return {
            ...dynamic,
            ...override,
            presets: [...(override.presets || []), ...(dynamic.presets || [])],
        } as FacetFieldConfig<TEntity>;
    });

    overrides.forEach((override) => {
        if (!merged.some(({ key }) => key === override.key)) {
            merged.push(
                overrideMap.get(override.key) as FacetFieldConfig<TEntity>,
            );
        }
    });

    return merged.sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
};
